import { useEffect, useState } from 'react';
import { api } from './api.js';

// Task reference photos for a location's task list (CJ 2026-09-30).
//
// Every TaskCard asks for its own task's photos, but the fetch happens ONCE
// per location — cards share the in-flight request and the result. The task
// editor calls invalidateTaskPhotos() after a change so the list refetches.
const cache = new Map();      // locationId -> { at, byTask: { [task_id]: [photo] } }
// The image links are signed for 4 hours; refetch well before that so a task
// list left open all shift doesn't end up with broken thumbnails.
const FRESH_MS = 60 * 60 * 1000;
const fresh = (locationId) => {
  const c = cache.get(locationId);
  return c && Date.now() - c.at < FRESH_MS;
};
const inflight = new Map();   // locationId -> Promise
const listeners = new Set();

function load(locationId) {
  if (!inflight.has(locationId)) {
    inflight.set(locationId, api('GET', `/api/task-photos?location_id=${locationId}`)
      .then((r) => { cache.set(locationId, { at: Date.now(), byTask: r?.photos || {} }); })
      // A failed load leaves the cards without thumbnails, which is exactly
      // what they showed before photos existed; the task list still works.
      .catch((e) => { console.warn('[task-photos] load failed:', e?.message || e); cache.set(locationId, { at: Date.now(), byTask: {} }); })
      .finally(() => { inflight.delete(locationId); listeners.forEach((fn) => fn()); }));
  }
  return inflight.get(locationId);
}

export function invalidateTaskPhotos(locationId) {
  cache.delete(locationId);
  load(locationId);
}

export function useTaskPhotos(locationId, taskId) {
  const [, rerender] = useState(0);
  useEffect(() => {
    const fn = () => rerender((n) => n + 1);
    listeners.add(fn);
    if (locationId && !fresh(locationId)) load(locationId);
    return () => { listeners.delete(fn); };
  }, [locationId]);
  return (cache.get(locationId)?.byTask || {})[taskId] || [];
}
